import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { labApi } from '@/api/lab';
import { Button } from "@/components/ui/button";
import { Eye, ArrowLeft, Clock, CheckCircle, AlertCircle, Download, FileText, Pencil } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import ViewTestResult from "../lab/ViewTestResult";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { appointmentApi } from '@/api/appointment';
import { calculateAge } from '@/utils/dateUtils';
import { hospitalApi } from '@/api/hospital';
import { Hospital } from '@/types/types';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import { formatPatientDisplayId } from '@/lib/patientDisplayId';
import { DEFAULT_HOSPITAL_LOGO_PATH, resolveHospitalLogoSrc } from '@/lib/hospitalLogo';
import { isDiagnosisWithinEditWindow } from '@/utils/diagnosisEditWindow';

function SectionTitle({ title }: { title: string }) {
    return (
        <div className="mb-4 flex items-center gap-3">
            <div className="h-8 w-1 rounded-full bg-gradient-to-b from-amber-400 to-amber-600" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-[#0c2340]">{title}</h2>
        </div>
    );
}

function InfoField({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
            <span className="text-sm font-semibold text-slate-900">{value}</span>
        </div>
    );
}

function DiagnosisRecord() {
    const { appointmentId } = useParams();
    const navigate = useNavigate();
    const [selectedTestForView, setSelectedTestForView] = useState<{ id: string, name: string } | null>(null);
    const [isViewTestResultDialogOpen, setIsViewTestResultDialogOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    // Fetch diagnosis record using useQuery
    const { data: diagnosisRecord, isLoading } = useQuery<any>({
        queryKey: ['diagnosis-record', appointmentId],
        queryFn: async () => {
            if (!appointmentId) throw new Error('Appointment ID is required');
            const response = await api.get(`/api/diagnosis/get-by-appointment/${appointmentId}`);
            return response.data.data;
        },
        enabled: !!appointmentId,
    });

    const { data: labTests } = useQuery<any>({
        queryKey: ['lab-tests', appointmentId],
        queryFn: async () => {
            if (!appointmentId) throw new Error('Appointment ID is required');
            const response = await labApi.getOrderedTestsByAppointment(appointmentId);
            return response.data?.data;
        },
    });

    // Fetch surgical information
    const { data: surgicalInfo } = useQuery<any>({
        queryKey: ['surgical-info', appointmentId],
        queryFn: async () => {
            if (!appointmentId) throw new Error('Appointment ID is required');
            const response = await appointmentApi.getSurgeryByAppointmentId(appointmentId);
            return response.data?.data;
        },
        enabled: !!appointmentId,
    });

    // Fetch hospital information
    const { data: hospital, isLoading: isHospitalLoading } = useQuery<Hospital>({
        queryKey: ['hospital', diagnosisRecord?.appointment?.hospitalId],
        queryFn: async () => {
            if (!diagnosisRecord?.appointment?.hospitalId) {
                throw new Error('Hospital ID is required');
            }
            const response = await hospitalApi.getHospitalById(diagnosisRecord.appointment.hospitalId);
            return response;
        },
        enabled: !!diagnosisRecord?.appointment?.hospitalId,
    });

    const handlePrint = async () => {
        if (!appointmentId) return;

        try {
            const response = await api.get(`/api/diagnosis/get-html/${appointmentId}`, {
                responseType: 'text'
            });

            // Create a new window for printing
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                toast.error('Please allow popups to print');
                return;
            }

            // Write the HTML content to the new window
            printWindow.document.write(response.data);
            printWindow.document.close();

            // Wait for all images to load before printing
            printWindow.onload = function () {
                printWindow.focus();
                printWindow.print();
                // Close the window after printing (optional)
                printWindow.onafterprint = function () {
                    printWindow.close();
                };
            };
        } catch (error) {
            console.error('Error getting print template:', error);
            toast.error('Failed to prepare document for printing');
        }
    };
    const handleDownloadPDF = async () => {
        if (!appointmentId) return;

        setIsDownloading(true);
        try {
            const response = await api.get(`/api/diagnosis/download-pdf/${appointmentId}`, {
                responseType: 'blob'
            });

            // Create a blob from the PDF Stream
            const file = new Blob([response.data], { type: 'application/pdf' });

            // Create a link element and trigger download
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = `diagnosis-record-${appointmentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileURL);

            toast.success('PDF downloaded successfully');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download PDF');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleViewTestResult = (testId: string, testName: string) => {
        setSelectedTestForView({ id: testId, name: testName });
        setIsViewTestResultDialogOpen(true);
    };

    if (isLoading || isHospitalLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Clock className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading diagnosis record...</p>
                </div>
            </div>
        );
    }

    if (!diagnosisRecord) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <AlertCircle className="h-8 w-8 text-yellow-500 mx-auto mb-4" />
                    <p className="text-gray-600">No diagnosis record found for this appointment.</p>
                    <Button
                        onClick={() => navigate(-1)}
                        variant="outline"
                        className="mt-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Go Back
                    </Button>
                </div>
            </div>
        );
    }

    const getStatusColor = (status: string) => {
        const colors = {
            PENDING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
            PROCESSING: 'bg-blue-50 text-blue-700 border-blue-200',
            COMPLETED: 'bg-green-50 text-green-700 border-green-200',
            default: 'bg-gray-50 text-gray-700 border-gray-200'
        };
        return colors[status as keyof typeof colors] || colors.default;
    };

    const hospitalLogoSrc = resolveHospitalLogoSrc(hospital);
    const patientId = diagnosisRecord?.appointment?.patient?.id;
    const canEdit =
        diagnosisRecord?.isEditable === true ||
        isDiagnosisWithinEditWindow(diagnosisRecord?.createdAt);

    return (
        <div className="min-h-screen bg-[#eef1f5] py-8 px-4 sm:px-6 lg:px-8 print:bg-white print:py-0">
            <div className="max-w-4xl mx-auto">
                {/* Header with Back Button and Download Button */}
                <div className="flex items-center justify-between mb-6 print:hidden">
                    <Button
                        onClick={() => navigate(-1)}
                        variant="outline"
                        className="flex items-center gap-2 border-slate-200 bg-white text-slate-600 shadow-sm hover:text-slate-900"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                    <div className="flex items-center gap-2">
                        {canEdit && patientId && appointmentId && (
                            <Button
                                onClick={() =>
                                    navigate(
                                        `/doctor/consultation/${patientId}/${appointmentId}?edit=1`
                                    )
                                }
                                variant="outline"
                                className="flex items-center gap-2 border-teal-200 bg-white text-teal-800 shadow-sm hover:bg-teal-50"
                            >
                                <Pencil className="h-4 w-4" />
                                Edit Diagnosis
                            </Button>
                        )}
                        <Button
                            onClick={handleDownloadPDF}
                            variant="outline"
                            className="flex items-center gap-2 border-slate-200 bg-white text-[#0c2340] shadow-sm hover:bg-slate-50"
                            disabled={isDownloading}
                        >
                            <Download className="h-4 w-4" />
                            {isDownloading ? 'Downloading…' : 'Download PDF'}
                        </Button>
                        <Button
                            onClick={handlePrint}
                            variant="outline"
                            className="flex items-center gap-2 border-slate-200 bg-white text-[#0c2340] shadow-sm hover:bg-slate-50"
                            title="Print using the formatted template"
                        >
                            <FileText className="h-4 w-4" />
                            Print Record
                        </Button>
                    </div>
                </div>

                {/* Print Styles */}
                <style type="text/css" media="print">{`
                    @page {
                        margin: 0;
                        size: A4;
                    }
                    body {
                        margin: 1.6cm;
                    }
                    .print:hidden {
                        display: none !important;
                    }
                    nav, footer, .no-print {
                        display: none !important;
                    }
                    .bg-gradient-to-r {
                        background: white !important;
                        -webkit-print-color-adjust: exact;
                    }
                    .shadow-lg {
                        box-shadow: none !important;
                    }
                    .bg-gray-50, .bg-gray-100 {
                        background-color: white !important;
                    }
                    table {
                        break-inside: auto !important;
                    }
                    tr {
                        break-inside: avoid !important;
                        break-after: auto !important;
                    }
                    thead {
                        display: table-header-group;
                    }
                    tfoot {
                        display: table-footer-group;
                    }
                `}</style>

                {/* Medical Record Document */}
                <div className="overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(12,35,64,0.08)] print:shadow-none print:border-0">
                    {/* Hospital Header */}
                    <div className="relative bg-gradient-to-br from-[#0c2340] via-[#1a3a5c] to-[#2d5a87] px-6 py-5 text-white">
                        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-amber-400 via-amber-200 to-amber-400" />
                        <div className="flex items-center justify-between gap-6">
                            <img
                                src={hospitalLogoSrc}
                                alt={hospital?.name ? `${hospital.name} logo` : 'Hospital logo'}
                                className="h-14 object-contain"
                                onError={(e) => {
                                    const img = e.currentTarget;
                                    if (!img.src.endsWith(DEFAULT_HOSPITAL_LOGO_PATH)) {
                                        img.src = DEFAULT_HOSPITAL_LOGO_PATH;
                                    }
                                }}
                            />
                            <div className="flex-1 text-right">
                                {hospital?.name && (
                                    <div className="text-xl font-bold tracking-wide">{hospital.name}</div>
                                )}
                                {hospital?.address && (
                                    <div className="mt-1 text-[11px] leading-snug text-white/80">{hospital.address}</div>
                                )}
                                {hospital?.contactNumber && (
                                    <div className="mt-0.5 text-[11px] text-white/75">{hospital.contactNumber}</div>
                                )}
                                {hospital?.gstin && (
                                    <div className="mt-0.5 text-[11px] text-white/75">GSTIN — {hospital.gstin}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Document band */}
                    <div className="flex items-center justify-between border-b border-slate-100 bg-[#fafaf8] px-6 py-3">
                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0c2340]">
                            OPD Consultation Record
                        </span>
                        <span className="text-[11px] text-slate-500">
                            {format(new Date(diagnosisRecord.createdAt), 'dd MMMM yyyy · hh:mm a')}
                        </span>
                    </div>

                    {/* Patient Information */}
                    <div className="border-b border-slate-100 px-6 py-5">
                        <SectionTitle title="Patient Information" />
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 rounded-lg border border-slate-100 bg-[#fafaf8] p-5 md:grid-cols-3">
                            <InfoField label="Patient Name" value={diagnosisRecord.appointment?.patient?.name} />
                            <InfoField
                                label="Age"
                                value={
                                    diagnosisRecord.appointment?.patient?.dob
                                        ? `${calculateAge(diagnosisRecord.appointment.patient.dob)} years`
                                        : 'N/A'
                                }
                            />
                            <InfoField label="Gender" value={diagnosisRecord.appointment?.patient?.gender} />
                            <InfoField label="Phone" value={diagnosisRecord.appointment?.patient?.phone} />
                            <InfoField label="UHID" value={formatPatientDisplayId(diagnosisRecord.appointment?.patient)} />
                            <InfoField
                                label="Registration"
                                value={`${diagnosisRecord.appointment?.patient?.registrationMode || 'OPD'} · ${diagnosisRecord.appointment?.patient?.registrationSource || 'WALK_IN'}`}
                            />
                        </div>
                    </div>

                    {/* Primary Diagnosis */}
                    <div className="border-b border-slate-100 px-6 py-5">
                        <SectionTitle title="Primary Diagnosis" />
                        <div className="rounded-lg border border-amber-100 border-l-4 border-l-amber-500 bg-gradient-to-br from-[#fffef9] to-[#faf8f0] px-5 py-4">
                            <p className="whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-slate-900">
                                {diagnosisRecord.diagnosis}
                            </p>
                        </div>
                    </div>

                    {/* Medical History Section */}
                    {(diagnosisRecord.appointment?.patient?.allergy ||
                        diagnosisRecord.appointment?.patient?.chronicDisease ||
                        diagnosisRecord.appointment?.patient?.preExistingCondition) && (
                            <div className="border-b border-slate-100 px-6 py-5">
                                <SectionTitle title="Medical History" />
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    {diagnosisRecord.appointment?.patient?.allergy && (
                                        <div className="rounded-lg border border-red-100 bg-red-50/60 p-4">
                                            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-wider text-red-800">Allergies</h4>
                                            <p className="text-sm text-red-900">{diagnosisRecord.appointment.patient.allergy}</p>
                                        </div>
                                    )}
                                    {diagnosisRecord.appointment?.patient?.chronicDisease && (
                                        <div className="rounded-lg border border-orange-100 bg-orange-50/60 p-4">
                                            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-wider text-orange-800">Chronic Diseases</h4>
                                            <p className="text-sm text-orange-900">{diagnosisRecord.appointment.patient.chronicDisease}</p>
                                        </div>
                                    )}
                                    {diagnosisRecord.appointment?.patient?.preExistingCondition && (
                                        <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-4">
                                            <h4 className="mb-1 text-[10px] font-bold uppercase tracking-wider text-amber-800">Pre-existing Conditions</h4>
                                            <p className="text-sm text-amber-900">{diagnosisRecord.appointment.patient.preExistingCondition}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    {/* Prescribed Medicines */}
                    <div className="border-b border-slate-100 px-6 py-5">
                        <SectionTitle title="Prescribed Medicines" />
                        {diagnosisRecord.medicines.length > 0 ? (
                            <div className="overflow-hidden rounded-lg border border-slate-200">
                                <table className="min-w-full">
                                    <thead>
                                        <tr className="bg-[#0c2340] text-white">
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">#</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Medicine</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Frequency</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {diagnosisRecord.medicines.map((medicine: any, index: number) => (
                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}>
                                                <td className="px-4 py-3 text-sm text-slate-600">{index + 1}</td>
                                                <td className="px-4 py-3 text-sm font-semibold text-slate-900">{medicine.name}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{medicine.frequency}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{medicine.duration} days</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm italic text-slate-500">
                                No medicines prescribed
                            </p>
                        )}
                    </div>

                    {/* Lab Tests Section */}
                    {labTests && labTests.length > 0 && (
                        <div className="border-b border-slate-100 px-6 py-5 print:hidden">
                            <SectionTitle title="Lab Investigations" />
                            <div className="overflow-hidden rounded-lg border border-slate-200">
                                <table className="min-w-full">
                                    <thead>
                                        <tr className="bg-[#0c2340] text-white">
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Test Name</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Report Date</th>
                                            <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {labTests.map((test: any, index: number) => (
                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/80'}>
                                                <td className="px-4 py-3 text-sm font-medium text-slate-900">{test.labTest.name}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${getStatusColor(test.status)}`}>
                                                        {test.status === 'COMPLETED' && <CheckCircle className="mr-1 h-3 w-3" />}
                                                        {test.status === 'PROCESSING' && <Clock className="mr-1 h-3 w-3 animate-spin" />}
                                                        {test.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-slate-600">
                                                    {test.tentativeReportDate ? format(new Date(test.tentativeReportDate), 'dd MMM yyyy') : '—'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {test.status === 'COMPLETED' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="flex items-center gap-1 border-slate-200"
                                                            onClick={() => handleViewTestResult(test.id, test.labTest.name)}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                            View
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Follow-up & Surgery */}
                    <div className="border-b border-slate-100 px-6 py-5">
                        <div className="grid grid-cols-1 gap-4 rounded-lg border border-slate-100 bg-[#fafaf8] p-4 md:grid-cols-2">
                            <div className="flex gap-3 text-sm">
                                <span className="min-w-[90px] text-[10px] font-bold uppercase tracking-wider text-[#0c2340]">Follow-up</span>
                                <span className="text-slate-700">
                                    {diagnosisRecord.followUpAppointment
                                        ? new Date(diagnosisRecord.followUpAppointment.scheduledAt)
                                            .toLocaleString('en-IN', {
                                                day: '2-digit',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true,
                                                timeZone: 'UTC',
                                            })
                                        : 'No follow-up required'}
                                </span>
                            </div>
                            <div className="flex gap-3 text-sm">
                                <span className="min-w-[90px] text-[10px] font-bold uppercase tracking-wider text-[#0c2340]">Surgery</span>
                                <span className="text-slate-700">
                                    {surgicalInfo && surgicalInfo.length > 0 && surgicalInfo[0].status !== 'NOT_REQUIRED'
                                        ? surgicalInfo.map((surgery: any) => (
                                            `${surgery.category} — ${surgery.status === 'CONFIRMED' ? 'Confirmed' : 'Pending'}${surgery.scheduledAt ? ` (${format(new Date(surgery.scheduledAt), 'dd MMM yyyy')})` : ''}`
                                        )).join(', ')
                                        : 'Non-surgical treatment'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Notes Section */}
                    {diagnosisRecord.notes && (
                        <div className="border-b border-slate-100 px-6 py-5">
                            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                                <SectionTitle title="Clinical Notes" />
                                <p className="whitespace-pre-wrap text-sm leading-relaxed text-amber-950">{diagnosisRecord.notes}</p>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-end justify-between border-t-2 border-amber-100 bg-[#fafaf8] px-6 py-5">
                        <div className="text-xs leading-relaxed text-slate-500">
                            <p>
                                Issued on{' '}
                                <span className="font-semibold text-slate-800">
                                    {format(new Date(diagnosisRecord.createdAt), 'dd MMMM yyyy, hh:mm a')}
                                </span>
                            </p>
                            <p className="mt-1">
                                Consulting physician:{' '}
                                <span className="font-semibold text-slate-800">
                                    {ensureDoctorPrefix(diagnosisRecord.appointment?.doctor?.name)}
                                </span>
                                {diagnosisRecord.appointment?.doctor?.specialisation && (
                                    <span className="text-slate-500"> · {diagnosisRecord.appointment.doctor.specialisation}</span>
                                )}
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="mb-8 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                Authorised Signature
                            </div>
                            <div className="ml-auto h-px w-36 bg-[#0c2340]" />
                        </div>
                    </div>
                </div>

                {/* View Test Result Dialog */}
                <Dialog open={isViewTestResultDialogOpen} onOpenChange={setIsViewTestResultDialogOpen}>
                    <DialogContent className="max-w-4xl">
                        <DialogHeader>
                            <DialogTitle>Test Result</DialogTitle>
                        </DialogHeader>
                        {selectedTestForView && (
                            <ViewTestResult
                                appointmentLabTestId={selectedTestForView.id}
                                testName={selectedTestForView.name}
                            />
                        )}
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsViewTestResultDialogOpen(false);
                                    setSelectedTestForView(null);
                                }}
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}

export default DiagnosisRecord;